import {useEffect, useMemo, useState} from 'react';
import {graphql, forecastFetchQuery} from '../../../graphql/forecastRelay';
import type {useFilterOptionsQuery$data} from './__generated__/useFilterOptionsQuery.graphql';
import {labelDisplayName} from '../utils/labelDisplay';

export interface FilterOptionItem {
	id: string;
	label: string;
	color?: string;
	group?: string;
}

const FilterOptionsQuery = graphql`
	query useFilterOptionsQuery {
		viewer {
			company {
				labels(first: 10000) {
					edges {
						node {
							id
							name
							color
							category {
								name
							}
						}
					}
				}
			}
			requesters {
				id
				name
				email
			}
			peopleSuggestions {
				id
				name
				email
			}
		}
	}
`;

export function useFilterOptions() {
	const [data, setData] = useState<useFilterOptionsQuery$data | null>(null);

	useEffect(() => {
		let cancelled = false;
		forecastFetchQuery(FilterOptionsQuery, {})
			.then(result => {
				if (cancelled) return;
				setData(result as useFilterOptionsQuery$data | null);
			})
			.catch(() => {
				if (cancelled) return;
				setData(null);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const labelOptions = useMemo<FilterOptionItem[]>(
		() =>
			(data?.viewer?.company?.labels?.edges ?? [])
				.map(e => e?.node)
				.filter((n): n is NonNullable<typeof n> => n != null)
				.map(l => ({
					id: l.id,
					label: labelDisplayName(l),
					color: l.color ?? undefined,
					group: l.category?.name ?? undefined,
				})),
		[data?.viewer?.company?.labels?.edges]
	);

	const requesterOptions = useMemo<FilterOptionItem[]>(
		() =>
			(data?.viewer?.requesters ?? []).map(r => ({
				id: r.id,
				label: r.name || r.email || '',
			})),
		[data?.viewer?.requesters]
	);

	// ACC-473: source the assignee filter from Forecast people (Person global
	// ids) so the filter pills carry the same ids the backend filters and the
	// assign mutations write by. The Forecast `tickets` resolver decodes these
	// Person global ids into the assignee_forecast_person_id backend filter.
	const assigneeOptions = useMemo<FilterOptionItem[]>(
		() =>
			(data?.viewer?.peopleSuggestions ?? []).map(p => ({
				id: p.id,
				label: p.name || p.email || '',
			})),
		[data?.viewer?.peopleSuggestions]
	);

	return {labelOptions, requesterOptions, assigneeOptions};
}
